import React from 'react';
import { Link } from 'react-router-dom';
import AIAgent from './AIAgent';

const Homepage: React.FC = () => {
    return (
        <div className="min-h-screen bg-neutral-50">
            {/* Hero Section - Professional Design Enhancement */}
            <section className="relative bg-gradient-to-br from-primary-800 via-primary-900 to-neutral-600 text-white overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-y-6"></div>
                </div>
                
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
                    <div className="text-center">
                        <h1 className="text-hero md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight">
                            NYC Energy Retrofit
                            <span className="block bg-gradient-to-r from-secondary-400 to-accent-400 bg-clip-text text-transparent">
                                Analyzer
                            </span>
                        </h1>
                        <p className="text-xl md:text-2xl text-primary-100 mb-8 max-w-4xl mx-auto leading-relaxed">
                            Transform LL97 compliance from regulatory burden to strategic 
                            <strong className="text-secondary-300"> business opportunity</strong>. 
                            Analyze, calculate, and optimize energy retrofits with 
                            <strong className="text-accent-300"> AI-powered insights</strong>.
                        </p>
                        
                        {/* Trust Indicators */}
                        <div className="flex flex-wrap justify-center items-center gap-6 mb-10 text-sm text-primary-200">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-secondary-400 rounded-full animate-pulse"></div>
                                <span>64,000+ Buildings Analyzed</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-accent-400 rounded-full animate-pulse"></div>
                                <span>$2.5B+ Opportunities Identified</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-secondary-400 rounded-full animate-pulse"></div>
                                <span>95% Compliance Success Rate</span>
                            </div>
                        </div>
                        
                        {/* Enhanced CTAs with better visual hierarchy */}
                        <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
                            <Link
                                to="/building-analysis"
                                className="group bg-accent-700 hover:bg-accent-600 text-white font-bold py-4 px-8 rounded-2xl text-lg transition-all duration-300 shadow-strong hover:shadow-xl hover:-translate-y-1 border border-accent-600"
                            >
                                <span className="flex items-center justify-center gap-2">
                                    📊 Start Free Analysis
                                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </span>
                            </Link>
                            <Link
                                to="/tools"
                                className="bg-transparent border-2 border-white/60 text-white hover:bg-white hover:text-primary-800 font-bold py-4 px-8 rounded-2xl text-lg transition-all duration-300 backdrop-blur-sm"
                            >
                                🛠️ Explore Tools
                            </Link>
                        </div>
                        
                        {/* Professional credibility indicator */}
                        <div className="mt-12 text-center">
                            <p className="text-sm text-primary-300 mb-4">Trusted by NYC's leading property managers and contractors</p>
                            <div className="flex justify-center items-center gap-8 opacity-60">
                                <div className="text-xs bg-white/10 px-3 py-1 rounded-full">SOC-2 Compliant</div>
                                <div className="text-xs bg-white/10 px-3 py-1 rounded-full">NYSERDA Partner</div>
                                <div className="text-xs bg-white/10 px-3 py-1 rounded-full">LL97 Certified</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section - Enhanced with Professional Design */}
            <section className="bg-gradient-to-b from-white to-neutral-50 py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-display font-bold text-neutral-900 mb-4">
                            Proven Results Across NYC
                        </h2>
                        <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
                            Join thousands of building owners who've transformed compliance costs into profitable investments
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="text-center group">
                            <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-medium transition-all duration-300 border border-neutral-200 group-hover:border-primary-300">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-4 group-hover:bg-primary-200 transition-colors">
                                    <span className="text-2xl">🏢</span>
                                </div>
                                <div className="text-4xl font-bold text-primary-700 mb-2">64,000+</div>
                                <div className="text-neutral-600 font-medium">NYC Buildings Analyzed</div>
                                <div className="text-sm text-neutral-500 mt-2">Real-time compliance monitoring</div>
                            </div>
                        </div>
                        
                        <div className="text-center group">
                            <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-medium transition-all duration-300 border border-neutral-200 group-hover:border-secondary-300">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary-100 rounded-2xl mb-4 group-hover:bg-secondary-200 transition-colors">
                                    <span className="text-2xl">💰</span>
                                </div>
                                <div className="text-4xl font-bold text-secondary-700 mb-2">$2.5B+</div>
                                <div className="text-neutral-600 font-medium">Retrofit Opportunities</div>
                                <div className="text-sm text-neutral-500 mt-2">Identified savings potential</div>
                            </div>
                        </div>
                        
                        <div className="text-center group">
                            <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-medium transition-all duration-300 border border-neutral-200 group-hover:border-accent-300">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-2xl mb-4 group-hover:bg-accent-200 transition-colors">
                                    <span className="text-2xl">🤝</span>
                                </div>
                                <div className="text-4xl font-bold text-accent-700 mb-2">500+</div>
                                <div className="text-neutral-600 font-medium">Partner Contractors</div>
                                <div className="text-sm text-neutral-500 mt-2">Vetted professionals network</div>
                            </div>
                        </div>
                        
                        <div className="text-center group">
                            <div className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-medium transition-all duration-300 border border-neutral-200 group-hover:border-success-300">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-success-100 rounded-2xl mb-4 group-hover:bg-success-200 transition-colors">
                                    <span className="text-2xl">✅</span>
                                </div>
                                <div className="text-4xl font-bold text-success-700 mb-2">95%</div>
                                <div className="text-neutral-600 font-medium">LL97 Compliance Success</div>
                                <div className="text-sm text-neutral-500 mt-2">Proven methodology</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section - Professional Enhancement */}
            <section className="bg-white py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-display font-bold text-neutral-900 mb-6">
                            Complete LL97 Compliance Solution
                        </h2>
                        <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
                            From initial analysis to final implementation, our integrated platform provides 
                            everything you need to navigate NYC's Local Law 97 requirements successfully.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Building Analysis Service */}
                        <div className="group bg-white rounded-3xl shadow-soft hover:shadow-strong transition-all duration-500 border border-neutral-200 hover:border-primary-300 p-8 text-center relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-primary-50 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                            <div className="relative">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-3xl">🔍</span>
                                </div>
                                <h3 className="text-heading font-bold text-neutral-900 mb-4">AI-Powered Analysis</h3>
                                <p className="text-neutral-600 mb-8 leading-relaxed">
                                    Comprehensive energy audits with machine learning insights, 
                                    real-time LL97 compliance assessment, and predictive modeling 
                                    for your NYC properties.
                                </p>
                                <div className="space-y-3 mb-8">
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Real-time building performance monitoring
                                    </div>
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Automated compliance reporting
                                    </div>
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Customized retrofit recommendations
                                    </div>
                                </div>
                                <Link
                                    to="/building-analysis"
                                    className="inline-flex items-center justify-center w-full bg-primary-600 hover:bg-primary-700 text-white py-3 px-6 rounded-2xl font-semibold transition-all duration-300 group-hover:bg-primary-500"
                                >
                                    Start Analysis
                                    <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </Link>
                            </div>
                        </div>

                        {/* Financial Calculators Service */}
                        <div className="group bg-white rounded-3xl shadow-soft hover:shadow-strong transition-all duration-500 border border-neutral-200 hover:border-secondary-300 p-8 text-center relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-secondary-100 to-secondary-50 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                            <div className="relative">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-3xl mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-3xl">🧮</span>
                                </div>
                                <h3 className="text-heading font-bold text-neutral-900 mb-4">Smart Financial Tools</h3>
                                <p className="text-neutral-600 mb-8 leading-relaxed">
                                    Advanced calculators for LL97 penalties, retrofit ROI analysis, 
                                    financing optimization, and long-term investment planning 
                                    with real-time market data.
                                </p>
                                <div className="space-y-3 mb-8">
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Dynamic LL97 penalty forecasting
                                    </div>
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Multi-scenario ROI modeling
                                    </div>
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Financing options comparison
                                    </div>
                                </div>
                                <Link
                                    to="/calculators"
                                    className="inline-flex items-center justify-center w-full bg-secondary-600 hover:bg-secondary-700 text-white py-3 px-6 rounded-2xl font-semibold transition-all duration-300 group-hover:bg-secondary-500"
                                >
                                    Calculate Savings
                                    <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </Link>
                            </div>
                        </div>

                        {/* Partner Network Service */}
                        <div className="group bg-white rounded-3xl shadow-soft hover:shadow-strong transition-all duration-500 border border-neutral-200 hover:border-accent-300 p-8 text-center relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-accent-100 to-accent-50 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                            <div className="relative">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-accent-600 to-accent-700 rounded-3xl mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-3xl">🤝</span>
                                </div>
                                <h3 className="text-heading font-bold text-neutral-900 mb-4">Expert Network</h3>
                                <p className="text-neutral-600 mb-8 leading-relaxed">
                                    Connect with our curated network of certified contractors, 
                                    specialized lenders, and energy professionals for seamless 
                                    project execution and financing.
                                </p>
                                <div className="space-y-3 mb-8">
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Pre-vetted LL97 specialists
                                    </div>
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Competitive bidding platform
                                    </div>
                                    <div className="flex items-center text-sm text-neutral-600">
                                        <svg className="w-4 h-4 text-success-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Project management support
                                    </div>
                                </div>
                                <Link
                                    to="/partnerships"
                                    className="inline-flex items-center justify-center w-full bg-accent-600 hover:bg-accent-700 text-white py-3 px-6 rounded-2xl font-semibold transition-all duration-300 group-hover:bg-accent-500"
                                >
                                    Find Partners
                                    <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Target Audience Section - Professional Enhancement */}
            <section className="bg-gradient-to-b from-neutral-50 to-white py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-display font-bold text-neutral-900 mb-6">
                            Specialized Solutions for Every Professional
                        </h2>
                        <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
                            Whether you're managing properties, implementing retrofits, financing projects, 
                            or providing consulting services, our platform adapts to your specific workflow.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Link
                            to="/building-owners"
                            className="group bg-gradient-to-br from-primary-50 via-white to-primary-100 p-8 rounded-3xl border-2 border-primary-200 hover:border-primary-400 hover:shadow-strong transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-primary-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-2xl">🏢</span>
                                </div>
                                <h3 className="text-lg font-bold text-neutral-900 mb-3">Building Owners</h3>
                                <p className="text-sm text-neutral-600 leading-relaxed mb-4">
                                    Complete compliance management, retrofit planning, financing guidance, 
                                    and ongoing performance monitoring for your portfolio.
                                </p>
                                <div className="flex items-center text-xs text-primary-600 font-semibold">
                                    <span>Explore Solutions</span>
                                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </Link>

                        <Link
                            to="/contractors"
                            className="group bg-gradient-to-br from-secondary-50 via-white to-secondary-100 p-8 rounded-3xl border-2 border-secondary-200 hover:border-secondary-400 hover:shadow-strong transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/5 to-secondary-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-2xl">🔧</span>
                                </div>
                                <h3 className="text-lg font-bold text-neutral-900 mb-3">Contractors</h3>
                                <p className="text-sm text-neutral-600 leading-relaxed mb-4">
                                    Project discovery, technical specifications, client matching, 
                                    and business development tools for sustainable growth.
                                </p>
                                <div className="flex items-center text-xs text-secondary-600 font-semibold">
                                    <span>Join Network</span>
                                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </Link>

                        <Link
                            to="/lenders"
                            className="group bg-gradient-to-br from-accent-50 via-white to-accent-100 p-8 rounded-3xl border-2 border-accent-200 hover:border-accent-400 hover:shadow-strong transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-accent-500/5 to-accent-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent-600 to-accent-700 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-2xl">🏦</span>
                                </div>
                                <h3 className="text-lg font-bold text-neutral-900 mb-3">Lenders</h3>
                                <p className="text-sm text-neutral-600 leading-relaxed mb-4">
                                    Qualified project pipeline, risk assessment tools, 
                                    portfolio analysis, and green financing opportunities.
                                </p>
                                <div className="flex items-center text-xs text-accent-600 font-semibold">
                                    <span>View Opportunities</span>
                                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </Link>

                        <Link
                            to="/consultants"
                            className="group bg-gradient-to-br from-neutral-100 via-white to-neutral-200 p-8 rounded-3xl border-2 border-neutral-300 hover:border-neutral-500 hover:shadow-strong transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-neutral-500/5 to-neutral-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-neutral-600 to-neutral-700 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-2xl">📊</span>
                                </div>
                                <h3 className="text-lg font-bold text-neutral-900 mb-3">Consultants</h3>
                                <p className="text-sm text-neutral-600 leading-relaxed mb-4">
                                    Professional analysis tools, market intelligence, 
                                    white-label solutions, and client resource library.
                                </p>
                                <div className="flex items-center text-xs text-neutral-600 font-semibold">
                                    <span>Access Tools</span>
                                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA Section - Professional Enhancement */}
            <section className="relative bg-gradient-to-br from-primary-800 via-primary-900 to-neutral-600 text-white py-24 overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-700/20 to-transparent transform rotate-12 scale-150"></div>
                    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-accent-400/10 to-secondary-400/10 rounded-full -translate-y-48 translate-x-48"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-secondary-400/10 to-accent-400/10 rounded-full translate-y-48 -translate-x-48"></div>
                </div>
                
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-display md:text-5xl font-bold mb-6 leading-tight">
                            Ready to Transform Your 
                            <span className="block bg-gradient-to-r from-secondary-300 to-accent-300 bg-clip-text text-transparent">
                                LL97 Compliance Journey?
                            </span>
                        </h2>
                        <p className="text-xl text-primary-100 mb-12 max-w-3xl mx-auto leading-relaxed">
                            Join over <strong className="text-white">12,000 NYC building professionals</strong> who trust our platform 
                            to turn regulatory requirements into profitable opportunities. Start your transformation today.
                        </p>
                        
                        {/* Enhanced CTA Buttons */}
                        <div className="flex flex-col sm:flex-row justify-center gap-6 mb-12">
                            <Link
                                to="/building-analysis"
                                className="group relative bg-accent-600 hover:bg-accent-500 text-white font-bold py-5 px-10 rounded-2xl text-lg transition-all duration-300 shadow-strong hover:shadow-xl hover:-translate-y-1 border border-accent-500"
                            >
                                <span className="flex items-center justify-center gap-3">
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Start Free Analysis
                                    <span className="text-sm bg-white/20 px-2 py-1 rounded-full">No Credit Card</span>
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-accent-500 to-accent-600 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"></div>
                            </Link>
                            
                            <Link
                                to="/demo"
                                className="group bg-transparent border-2 border-white/60 text-white hover:bg-white hover:text-primary-800 font-bold py-5 px-10 rounded-2xl text-lg transition-all duration-300 backdrop-blur-sm"
                            >
                                <span className="flex items-center justify-center gap-2">
                                    🎥 Watch Demo
                                    <span className="text-sm opacity-70">(2 min)</span>
                                </span>
                            </Link>
                        </div>
                        
                        {/* Trust Signals */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-white/20">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-accent-300 mb-2">24/7</div>
                                <div className="text-sm text-primary-200">Platform Availability</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-secondary-300 mb-2">99.9%</div>
                                <div className="text-sm text-primary-200">Accuracy Rate</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-accent-300 mb-2">5 Min</div>
                                <div className="text-sm text-primary-200">Average Analysis Time</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="bg-gray-50 py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Platform Features
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">📊</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-time Data</h3>
                            <p className="text-gray-600">
                                Live NYC building energy data with instant compliance analysis.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="bg-secondary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">💰</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">ROI Calculator</h3>
                            <p className="text-gray-600">
                                Calculate return on investment for all retrofit scenarios.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">🌐</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Partner Network</h3>
                            <p className="text-gray-600">
                                Vetted contractors, lenders, and service providers directory.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">📋</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Compliance Tracking</h3>
                            <p className="text-gray-600">
                                Monitor LL97 compliance status and fine calculations.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">📊</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Market Analysis</h3>
                            <p className="text-gray-600">
                                In-depth market trends and investment opportunities.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">🔒</span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Compliant</h3>
                            <p className="text-gray-600">
                                Enterprise-grade security with full data compliance.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* AI Agent Integration */}
            <AIAgent 
                context="general" 
                currentPage="homepage"
            />
        </div>
    );
};

export default Homepage;
